// src/platform/pwa/updateManager.js

import {
  eventBus,
  logger
} from '@/platform/core'

class UpdateManager {
  constructor() {
    this.registration = null
    this.waitingWorker = null
    this.controllerChangeBound = false
  }

  setRegistration(registration) {
    this.registration = registration || null

    if (registration?.waiting) {
      this.setWaitingWorker(
        registration.waiting,
        registration
      )
    }
  }

  setWaitingWorker(
    worker,
    registration = this.registration
  ) {
    if (!worker) {
      return
    }

    this.waitingWorker = worker

    logger.info(
      'Application update is available.'
    )

    eventBus.emit(
      'platform.update.available',
      {
        worker,
        registration,
        timestamp:
          new Date().toISOString()
      }
    )
  }

  hasUpdate() {
    return Boolean(
      this.waitingWorker ||
      this.registration?.waiting
    )
  }

  applyUpdate() {
    const worker =
      this.waitingWorker ||
      this.registration?.waiting

    if (!worker) {
      logger.warn(
        'Update requested but no waiting service worker exists.'
      )

      return false
    }

    logger.info(
      'Applying application update.'
    )

    eventBus.emit(
      'platform.update.applying',
      {
        timestamp:
          new Date().toISOString()
      }
    )

    worker.postMessage({
      type: 'SKIP_WAITING'
    })

    return true
  }

  async checkForUpdate() {
    if (!this.registration) {
      logger.warn(
        'Unable to check for update because service worker registration is unavailable.'
      )

      return false
    }

    try {
      logger.debug(
        'Checking for application update.'
      )

      await this.registration.update()

      eventBus.emit(
        'platform.update.checked',
        {
          updateAvailable:
            this.hasUpdate(),
          timestamp:
            new Date().toISOString()
        }
      )

      return true
    } catch (error) {
      logger.error(
        'Failed to check for application update.',
        {
          error
        }
      )

      eventBus.emit(
        'platform.update.error',
        {
          operation: 'check',
          error
        }
      )

      return false
    }
  }

  watchControllerChange() {
    if (
      this.controllerChangeBound ||
      typeof navigator === 'undefined' ||
      !('serviceWorker' in navigator)
    ) {
      return
    }

    let refreshing = false

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        if (refreshing) {
          return
        }

        refreshing = true

        logger.info(
          'New service worker has taken control.'
        )

        eventBus.emit(
          'platform.update.activated',
          {
            timestamp:
              new Date().toISOString()
          }
        )

        window.location.reload()
      }
    )

    this.controllerChangeBound = true

    logger.debug(
      'Service worker controller-change listener registered.'
    )
  }

  getStatus() {
    return {
      registered:
        Boolean(this.registration),

      updateAvailable:
        this.hasUpdate(),

      hasWaitingWorker:
        Boolean(this.waitingWorker)
    }
  }

  clearWaitingWorker() {
    this.waitingWorker = null
  }
}

const updateManager =
  new UpdateManager()

export default updateManager